import { h, clear, formatDate, localInputToIso, isoToLocalInput, addDaysLocal } from '../ui/dom.js';
import {
  card,
  button,
  field,
  input,
  select,
  table,
  emptyState,
  openModal,
  toast,
  editionStatusBadge,
  metric,
  confirmDialog,
  badge,
} from '../ui/components.js';
import { api, ApiError } from '../api.js';
import { can } from '../store.js';
import type { Page, PageContext } from '../page.js';
import {
  EDITION_STATUS_LABELS,
  type Edition,
  type EditionStatus,
  type Template,
} from '../types.js';

export async function editionsPage(context: PageContext): Promise<Page> {
  const { items } = await api.get<{ items: Edition[] }>('/api/editions');

  const ativas = items.filter((edition) => edition.status === 'ATIVA').length;
  const futuras = items.filter((edition) => new Date(edition.startDate) >= new Date()).length;

  const rows = items.map((edition) => [
    h(
      'div',
      { class: 'stack stack--sm' },
      h('div', { style: { fontWeight: '600' }, text: edition.name }),
      h('div', { class: 'muted small', text: edition.location ?? 'Local não informado' }),
    ),
    h('div', { class: 'mono', text: formatDate(edition.startDate) }),
    h(
      'div',
      null,
      edition.days && edition.days.length > 1
        ? badge(`${edition.days.length} dias`, 'accent')
        : badge('1 dia', 'neutral'),
    ),
    h('div', null, editionStatusBadge(edition.status)),
    h(
      'div',
      { class: 'table__actions' },
      button('Agenda', {
        size: 'sm',
        variant: 'ghost',
        onClick: () => context.navigate(`/edicoes/${edition.id}/agenda`),
      }),
      button('Prontidão', {
        size: 'sm',
        variant: 'ghost',
        onClick: () => context.navigate(`/edicoes/${edition.id}/prontidao`),
      }),
      button('Apoio', {
        size: 'sm',
        variant: 'ghost',
        onClick: () => context.navigate(`/edicoes/${edition.id}/apoio`),
      }),
      button('Abrir', {
        size: 'sm',
        variant: 'subtle',
        onClick: () => context.navigate(`/edicoes/${edition.id}`),
      }),
    ),
  ]);

  const actions = can.createEditions()
    ? [button('Nova edição', { onClick: () => openEditionModal(() => window.location.reload()) })]
    : [];

  if (context.query.get('nova') === '1' && can.createEditions()) {
    openEditionModal(() => window.location.reload());
  }

  return {
    title: 'Edições do Encontro',
    subtitle: 'Instâncias reais criadas a partir dos modelos mestre, com escalas e grade horária.',
    actions,
    body: [
      h(
        'div',
        { class: 'grid grid--4' },
        metric('Edições', items.length, 'accent'),
        metric('Ativas', ativas, 'success'),
        metric('Futuras', futuras, 'info'),
        metric('Encerradas', items.filter((e) => e.status === 'ENCERRADA').length),
      ),
      card({
        title: 'Todas as edições',
        hint: can.createEditions()
          ? 'Você pode criar edições e montar escalas.'
          : 'Seu perfil tem acesso somente leitura às edições.',
        body:
          items.length > 0
            ? table(['Edição', 'Início', 'Status', ''], rows)
            : emptyState('Nenhuma edição cadastrada ainda.', '▤'),
      }),
    ],
  };
}

export function openEditionModal(onCreated: () => void): void {
  const nameInput = input({ placeholder: 'Ex.: Retiro de Homens — Outubro 2026' });
  const locationInput = input({ placeholder: 'Local do encontro' });
  const statusSelect = select(
    (Object.keys(EDITION_STATUS_LABELS) as EditionStatus[]).map((status) => ({
      value: status,
      label: EDITION_STATUS_LABELS[status],
    })),
    { value: 'PLANEJADA' },
  );
  const templateSelect = select([{ value: '', label: 'Carregando modelos…' }]);
  const errorSlot = h('div', { class: 'field__error' });

  /* ---------- Dias do encontro (agenda múltipla) ---------------------- */

  interface DayDraft {
    start: string;
    label: string;
  }

  const days: DayDraft[] = [{ start: '', label: 'Dia 1' }];
  const daysContainer = h('div', { class: 'stack' });
  const daysSummary = h('div', { class: 'muted small' });

  const renderDays = (): void => {
    clear(daysContainer);
    daysSummary.textContent =
      days.length === 1
        ? 'Encontro de 1 dia.'
        : `Encontro de ${days.length} dias — cada dia tem seu próprio horário de início.`;

    days.forEach((day, index) => {
      const dateField = input({
        type: 'datetime-local',
        value: day.start,
        attrs: { required: 'true' },
        oninput: (event: Event) => {
          day.start = (event.target as HTMLInputElement).value;
          // Ao definir o Dia 1, sugere automaticamente os dias seguintes.
          if (index === 0) syncFollowingDays();
        },
      });

      const labelField = input({
        value: day.label,
        placeholder: `Dia ${index + 1}`,
        oninput: (event: Event) => {
          day.label = (event.target as HTMLInputElement).value;
        },
      });

      daysContainer.append(
        h(
          'div',
          { class: 'card', style: { padding: '12px' } },
          h(
            'div',
            { class: 'row row--between', style: { marginBottom: '8px' } },
            h('div', { class: 'badge badge--accent', text: `Dia ${index + 1}` }),
            days.length > 1
              ? button('Remover', {
                  size: 'sm',
                  variant: 'ghost',
                  onClick: () => {
                    days.splice(index, 1);
                    renderDays();
                  },
                })
              : null,
          ),
          h(
            'div',
            { class: 'form-grid' },
            field({ label: 'Início das atividades', control: dateField, required: true }),
            field({
              label: 'Rótulo',
              control: labelField,
              hint: 'Ex.: Sexta à noite, Sábado manhã, Domingo.',
            }),
          ),
        ),
      );
    });
  };

  /** Replica o horário do Dia 1 nos dias seguintes, somando um dia. */
  const syncFollowingDays = (): void => {
    const first = days[0];
    if (!first?.start) return;
    const iso = localInputToIso(first.start);
    if (!iso) return;

    for (let index = 1; index < days.length; index += 1) {
      const day = days[index];
      if (!day || day.start) continue;
      const shifted = addDaysLocal(iso, index);
      day.start = isoToLocalInput(shifted);
    }
    renderDays();
  };

  const addDay = (): void => {
    const previous = days[days.length - 1];
    let suggested = '';
    const firstIso = days[0]?.start ? localInputToIso(days[0].start) : null;

    if (firstIso) {
      suggested = isoToLocalInput(addDaysLocal(firstIso, days.length));
    } else if (previous) {
      suggested = previous.start;
    }

    days.push({ start: suggested, label: `Dia ${days.length + 1}` });
    renderDays();
  };

  renderDays();

  /* ---------- Carregamento dos modelos --------------------------------- */

  void (async () => {
    try {
      const { items } = await api.get<{ items: Template[] }>('/api/templates');
      clear(templateSelect);
      if (items.length === 0) {
        templateSelect.append(h('option', { value: '', text: 'Nenhum modelo disponível' }));
        return;
      }
      for (const template of items) {
        templateSelect.append(
          h('option', {
            value: template.id,
            text: `${template.name} (${template.ministrationsCount ?? 0} ministrações)`,
          }),
        );
      }
    } catch {
      clear(templateSelect);
      templateSelect.append(h('option', { value: '', text: 'Falha ao carregar modelos' }));
    }
  })();

  /* ---------- Envio ---------------------------------------------------- */

  const submit = async (): Promise<void> => {
    clear(errorSlot);

    if (days.some((day) => !day.start)) {
      errorSlot.textContent = 'Informe a data e hora de início de todos os dias.';
      return;
    }

    const dayPayload = days.map((day, index) => ({
      dayNumber: index + 1,
      startAt: localInputToIso(day.start) ?? new Date(day.start).toISOString(),
      label: day.label.trim() || `Dia ${index + 1}`,
    }));

    try {
      await api.post<Edition>('/api/editions', {
        templateId: templateSelect.value,
        name: nameInput.value.trim(),
        location: locationInput.value.trim() || null,
        startDate: dayPayload[0]?.startAt,
        status: statusSelect.value,
        days: dayPayload,
      });
      modal?.close();
      toast.success(
        'Edição criada',
        `${dayPayload.length} dia(s) com agenda gerada a partir do modelo.`,
      );
      onCreated();
    } catch (error) {
      errorSlot.textContent =
        error instanceof ApiError ? error.message : 'Falha ao criar a edição.';
    }
  };

  let modal: { close: () => void } | null = null;
  modal = openModal({
    title: 'Nova edição do encontro',
    wide: true,
    body: [
      h('div', {
        class: 'muted small',
        text: 'A edição replica as ministrações do modelo e calcula a agenda automaticamente, com um cronograma por dia.',
      }),
      field({
        label: 'Modelo mestre',
        control: templateSelect,
        required: true,
        hint: 'O dia de cada ministração é definido no modelo e replicado aqui.',
      }),
      h(
        'div',
        { class: 'form-grid' },
        field({ label: 'Nome da edição', control: nameInput, required: true }),
        field({ label: 'Local', control: locationInput }),
        field({ label: 'Status', control: statusSelect }),
      ),
      h('hr', { class: 'separator' }),
      h(
        'div',
        { class: 'row row--between' },
        h(
          'div',
          null,
          h('div', { class: 'card__title', text: 'Agenda — dias do encontro' }),
          daysSummary,
        ),
        button('+ Adicionar dia', { size: 'sm', variant: 'ghost', onClick: addDay }),
      ),
      daysContainer,
      errorSlot,
    ],
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal?.close() }),
      button('Criar edição', { onClick: () => void submit() }),
    ],
  });
}

export function confirmEditionRemoval(edition: Edition, onConfirm: () => void): void {
  confirmDialog({
    title: 'Excluir edição',
    message: `Excluir "${edition.name}"? Todas as escalas, mídias e o histórico de prontidão vinculados serão removidos.`,
    confirmLabel: 'Excluir edição',
    danger: true,
    onConfirm,
  });
}
